/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Global Theme Studio & Palette Customizer View (Task 11.5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  Palette,
  CheckCircle2,
  Sparkles,
  RefreshCw,
  Plus,
  Trash2,
  Sliders,
  Eye,
  Save,
  Undo2,
  X,
  AlertCircle,
  Sun,
  Moon,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';
import type { AdminThemeItem, ThemeColorTokens } from '@sofia/shared-types';

export const ThemeStudioView: React.FC = () => {
  const { t } = useI18n();
  const domains = getAdminDomains();

  const [themes, setThemes] = useState<AdminThemeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Selected theme for editing
  const [selectedTheme, setSelectedTheme] = useState<AdminThemeItem | null>(null);
  const [editTokens, setEditTokens] = useState<{
    light: ThemeColorTokens;
    dark: ThemeColorTokens;
  } | null>(null);
  const [editMode, setEditMode] = useState<'light' | 'dark'>('light');

  // Create Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  const [newThemeDesc, setNewThemeDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Toast State
  const [toastMsg, setToastMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/themes`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = (await res.json()) as { success: boolean; data: AdminThemeItem[] };
      if (data.success && Array.isArray(data.data)) {
        setThemes(data.data);
        // Default select active theme or first theme if none selected
        if (!selectedTheme && data.data.length > 0) {
          const active = data.data.find((th) => th.isActive) || data.data[0];
          if (active) {
            setSelectedTheme(active);
            setEditTokens(JSON.parse(JSON.stringify(active.tokens)));
          }
        }
      }
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: err?.message || 'Failed to load themes',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes();
  }, []);

  const handleSelectTheme = (theme: AdminThemeItem) => {
    setSelectedTheme(theme);
    setEditTokens(JSON.parse(JSON.stringify(theme.tokens)));
  };

  const handleActivateTheme = async (theme: AdminThemeItem) => {
    try {
      setActivatingId(theme.id);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/themes/${theme.id}/activate`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      const result = (await res.json()) as { success: boolean; error?: string };
      if (!res.ok || !result.success) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      // Update local active state
      setThemes((prev) =>
        prev.map((item) => ({
          ...item,
          isActive: item.id === theme.id,
        }))
      );
      if (selectedTheme?.id === theme.id) {
        setSelectedTheme({ ...selectedTheme, isActive: true });
      }

      setToastMsg({
        type: 'success',
        text: t('admin.themeStudio.toast.activateSuccess', { name: theme.name }),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: t('admin.themeStudio.toast.activateError', { error: err?.message || 'Unknown error' }),
      });
    } finally {
      setActivatingId(null);
    }
  };

  const handleSaveTheme = async () => {
    if (!selectedTheme || !editTokens) return;
    try {
      setSavingId(selectedTheme.id);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/themes/${selectedTheme.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          tokens: editTokens,
        }),
      });

      const result = (await res.json()) as { success: boolean; data?: AdminThemeItem; error?: string };
      if (!res.ok || !result.success) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      setThemes((prev) =>
        prev.map((item) => (item.id === selectedTheme.id ? { ...item, tokens: editTokens } : item))
      );
      setSelectedTheme({ ...selectedTheme, tokens: editTokens });

      setToastMsg({
        type: 'success',
        text: t('admin.themeStudio.toast.saveSuccess', { name: selectedTheme.name }),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: err?.message || 'Failed to save theme',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteTheme = async (theme: AdminThemeItem) => {
    if (!confirm(t('common.confirm'))) return;
    try {
      setDeletingId(theme.id);
      const token = getAdminToken() || '';
      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/themes/${theme.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      const result = (await res.json()) as { success: boolean; error?: string };
      if (!res.ok || !result.success) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      setThemes((prev) => prev.filter((item) => item.id !== theme.id));
      if (selectedTheme?.id === theme.id) {
        const remaining = themes.filter((item) => item.id !== theme.id);
        if (remaining.length > 0 && remaining[0]) {
          handleSelectTheme(remaining[0]);
        } else {
          setSelectedTheme(null);
          setEditTokens(null);
        }
      }

      setToastMsg({
        type: 'success',
        text: t('admin.themeStudio.toast.deleteSuccess'),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: err?.message || 'Failed to delete theme',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateTheme = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newThemeName.trim()) return;

    try {
      setCreating(true);
      const token = getAdminToken() || '';
      const templateTokens = selectedTheme?.tokens || editTokens;

      const res = await fetch(`${domains.apiOrigin}/api/v1/admin/themes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          name: newThemeName.trim(),
          description: newThemeDesc.trim(),
          tokens: templateTokens,
        }),
      });

      const result = (await res.json()) as { success: boolean; data?: AdminThemeItem; error?: string };
      if (!res.ok || !result.success || !result.data) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      setThemes((prev) => [...prev, result.data!]);
      handleSelectTheme(result.data!);
      setIsCreateModalOpen(false);
      setNewThemeName('');
      setNewThemeDesc('');

      setToastMsg({
        type: 'success',
        text: t('admin.themeStudio.toast.createSuccess', { name: result.data.name }),
      });
    } catch (err: any) {
      setToastMsg({
        type: 'error',
        text: err?.message || 'Failed to create theme',
      });
    } finally {
      setCreating(false);
    }
  };

  const updateTokenField = (mode: 'light' | 'dark', field: keyof ThemeColorTokens, value: string) => {
    if (!editTokens) return;
    setEditTokens({
      ...editTokens,
      [mode]: {
        ...editTokens[mode],
        [field]: value,
      },
    });
  };

  const activeTokens = editTokens ? editTokens[editMode] : null;

  return (
    <div className="space-y-6">
      {/* Toast Notification Banner */}
      {toastMsg && (
        <div
          className={`p-4 rounded-xl flex items-center justify-between text-xs font-medium border ${
            toastMsg.type === 'success'
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}
        >
          <div className="flex items-center gap-2">
            {toastMsg.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0" />
            )}
            <span>{toastMsg.text}</span>
          </div>
          <button
            onClick={() => setToastMsg(null)}
            className="hover:opacity-75 transition-opacity p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Palette className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-heading font-bold text-foreground">
              {t('admin.themeStudio.title')}
            </h1>
          </div>
          <p className="text-xs text-muted-fg mt-1">
            {t('admin.themeStudio.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchThemes}
            disabled={loading}
            className="px-3.5 py-2 text-xs font-medium text-foreground bg-surface border border-surface-border rounded-xl hover:bg-surface-elevated transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary-hover rounded-xl shadow-glow-primary transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('admin.themeStudio.actions.createNew')}
          </button>
        </div>
      </div>

      {/* Theme Catalog Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {themes.map((theme) => {
          const isSelected = selectedTheme?.id === theme.id;
          return (
            <div
              key={theme.id}
              onClick={() => handleSelectTheme(theme)}
              className={`p-5 rounded-2xl border transition-all cursor-pointer relative flex flex-col justify-between ${
                isSelected
                  ? 'border-primary shadow-glow-primary/20 bg-surface-elevated ring-1 ring-primary'
                  : 'border-surface-border bg-surface hover:border-surface-border/80 hover:bg-surface-elevated/50'
              }`}
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-heading font-semibold text-sm text-foreground">
                    {theme.name}
                  </h3>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {theme.isPreset ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-primary/10 text-primary border border-primary/20">
                        {t('admin.themeStudio.presetBadge')}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold bg-accent/10 text-accent border border-accent/20">
                        {t('admin.themeStudio.customBadge')}
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-fg mb-4 line-clamp-2">
                  {theme.description || 'No description provided.'}
                </p>

                {/* Swatches preview */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Sun className="w-3.5 h-3.5 text-muted-fg" />
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-5 h-5 rounded-md border border-white/20 shadow-sm"
                        style={{ backgroundColor: `hsl(${theme.tokens.light.primary})` }}
                        title="Light Primary"
                      />
                      <div
                        className="w-5 h-5 rounded-md border border-white/20 shadow-sm"
                        style={{ backgroundColor: `hsl(${theme.tokens.light.accent})` }}
                        title="Light Accent"
                      />
                      <div
                        className="w-5 h-5 rounded-md border border-white/20 shadow-sm"
                        style={{ backgroundColor: `hsl(${theme.tokens.light.surface})` }}
                        title="Light Surface"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Moon className="w-3.5 h-3.5 text-muted-fg" />
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-5 h-5 rounded-md border border-white/20 shadow-sm"
                        style={{ backgroundColor: `hsl(${theme.tokens.dark.primary})` }}
                        title="Dark Primary"
                      />
                      <div
                        className="w-5 h-5 rounded-md border border-white/20 shadow-sm"
                        style={{ backgroundColor: `hsl(${theme.tokens.dark.accent})` }}
                        title="Dark Accent"
                      />
                      <div
                        className="w-5 h-5 rounded-md border border-white/20 shadow-sm"
                        style={{ backgroundColor: `hsl(${theme.tokens.dark.surface})` }}
                        title="Dark Surface"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Footer Actions */}
              <div className="pt-3 border-t border-surface-border flex items-center justify-between gap-2">
                {theme.isActive ? (
                  <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-400">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t('admin.themeStudio.activeBadge')}
                  </span>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActivateTheme(theme);
                    }}
                    disabled={activatingId === theme.id}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary-hover rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Sparkles className="w-3 h-3" />
                    {activatingId === theme.id
                      ? t('admin.themeStudio.actions.activating')
                      : t('admin.themeStudio.actions.activate')}
                  </button>
                )}

                {!theme.isPreset && !theme.isActive && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTheme(theme);
                    }}
                    disabled={deletingId === theme.id}
                    className="p-1.5 text-muted-fg hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                    title={t('admin.themeStudio.actions.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Editor & Live Preview Split Pane */}
      {selectedTheme && editTokens && activeTokens && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4">
          {/* Left: Token Customizer Editor (7 cols) */}
          <div className="lg:col-span-7 bg-surface border border-surface-border rounded-2xl p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-surface-border">
              <div>
                <div className="flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-primary" />
                  <h2 className="text-base font-heading font-bold text-foreground">
                    {selectedTheme.name}
                  </h2>
                </div>
                <p className="text-xs text-muted-fg mt-0.5">
                  {selectedTheme.isPreset
                    ? t('admin.themeStudio.presetBadge')
                    : t('admin.themeStudio.customBadge')}
                </p>
              </div>

              {/* Mode Toggle Tabs */}
              <div className="flex items-center p-1 bg-surface-elevated border border-surface-border rounded-xl">
                <button
                  type="button"
                  onClick={() => setEditMode('light')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                    editMode === 'light'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-muted-fg hover:text-foreground'
                  }`}
                >
                  <Sun className="w-3.5 h-3.5" />
                  {t('admin.themeStudio.colorPickers.modeLight')}
                </button>
                <button
                  type="button"
                  onClick={() => setEditMode('dark')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                    editMode === 'dark'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-muted-fg hover:text-foreground'
                  }`}
                >
                  <Moon className="w-3.5 h-3.5" />
                  {t('admin.themeStudio.colorPickers.modeDark')}
                </button>
              </div>
            </div>

            {/* Token Inputs Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Primary */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground flex items-center justify-between">
                  <span>{t('admin.themeStudio.colorPickers.primary')}</span>
                  <span
                    className="w-4 h-4 rounded-full border border-white/20"
                    style={{ backgroundColor: `hsl(${activeTokens.primary})` }}
                  />
                </label>
                <input
                  type="text"
                  value={activeTokens.primary}
                  onChange={(e) => updateTokenField(editMode, 'primary', e.target.value)}
                  placeholder="e.g. 262 83% 58%"
                  className="w-full px-3 py-2 text-xs font-mono bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Primary Hover */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground flex items-center justify-between">
                  <span>{t('admin.themeStudio.colorPickers.primaryHover')}</span>
                  <span
                    className="w-4 h-4 rounded-full border border-white/20"
                    style={{ backgroundColor: `hsl(${activeTokens.primaryHover})` }}
                  />
                </label>
                <input
                  type="text"
                  value={activeTokens.primaryHover}
                  onChange={(e) => updateTokenField(editMode, 'primaryHover', e.target.value)}
                  placeholder="e.g. 262 83% 52%"
                  className="w-full px-3 py-2 text-xs font-mono bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Accent */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground flex items-center justify-between">
                  <span>{t('admin.themeStudio.colorPickers.accent')}</span>
                  <span
                    className="w-4 h-4 rounded-full border border-white/20"
                    style={{ backgroundColor: `hsl(${activeTokens.accent})` }}
                  />
                </label>
                <input
                  type="text"
                  value={activeTokens.accent}
                  onChange={(e) => updateTokenField(editMode, 'accent', e.target.value)}
                  placeholder="e.g. 199 89% 48%"
                  className="w-full px-3 py-2 text-xs font-mono bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Accent Hover */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground flex items-center justify-between">
                  <span>{t('admin.themeStudio.colorPickers.accentHover')}</span>
                  <span
                    className="w-4 h-4 rounded-full border border-white/20"
                    style={{ backgroundColor: `hsl(${activeTokens.accentHover})` }}
                  />
                </label>
                <input
                  type="text"
                  value={activeTokens.accentHover}
                  onChange={(e) => updateTokenField(editMode, 'accentHover', e.target.value)}
                  placeholder="e.g. 199 89% 42%"
                  className="w-full px-3 py-2 text-xs font-mono bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Surface */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground flex items-center justify-between">
                  <span>{t('admin.themeStudio.colorPickers.surface')}</span>
                  <span
                    className="w-4 h-4 rounded-full border border-white/20"
                    style={{ backgroundColor: `hsl(${activeTokens.surface})` }}
                  />
                </label>
                <input
                  type="text"
                  value={activeTokens.surface}
                  onChange={(e) => updateTokenField(editMode, 'surface', e.target.value)}
                  placeholder="e.g. 0 0% 100%"
                  className="w-full px-3 py-2 text-xs font-mono bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Border */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground flex items-center justify-between">
                  <span>{t('admin.themeStudio.colorPickers.border')}</span>
                  <span
                    className="w-4 h-4 rounded-full border border-white/20"
                    style={{ backgroundColor: `hsl(${activeTokens.border})` }}
                  />
                </label>
                <input
                  type="text"
                  value={activeTokens.border}
                  onChange={(e) => updateTokenField(editMode, 'border', e.target.value)}
                  placeholder="e.g. 220 13% 91%"
                  className="w-full px-3 py-2 text-xs font-mono bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-surface-border">
              <button
                type="button"
                onClick={() => setEditTokens(JSON.parse(JSON.stringify(selectedTheme.tokens)))}
                className="px-3 py-2 text-xs font-medium text-muted-fg hover:text-foreground flex items-center gap-1.5 transition-colors"
              >
                <Undo2 className="w-3.5 h-3.5" />
                {t('admin.themeStudio.actions.restoreDefault')}
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveTheme}
                  disabled={savingId === selectedTheme.id}
                  className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary-hover rounded-xl shadow-glow-primary transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" />
                  {savingId === selectedTheme.id
                    ? t('admin.themeStudio.actions.saving')
                    : t('admin.themeStudio.actions.saveChanges')}
                </button>
              </div>
            </div>
          </div>

          {/* Right: Live Interactive Component Preview (5 cols) */}
          <div className="lg:col-span-5 bg-surface border border-surface-border rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-surface-border">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-accent" />
                <h3 className="text-sm font-heading font-bold text-foreground">
                  {t('admin.themeStudio.preview.title')}
                </h3>
              </div>
              <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20">
                {editMode.toUpperCase()} MODE
              </span>
            </div>

            {/* Simulated Workspace Box with inline dynamic tokens */}
            <div
              className="p-5 rounded-2xl border transition-all space-y-4"
              style={{
                backgroundColor: `hsl(${activeTokens.bgBase})`,
                borderColor: `hsl(${activeTokens.border})`,
                color: `hsl(${activeTokens.textMain})`,
              }}
            >
              {/* Header Banner */}
              <div className="flex items-center justify-between">
                <div
                  className="font-heading font-extrabold text-sm tracking-tight bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, hsl(${activeTokens.primary}), hsl(${activeTokens.accent}))`,
                  }}
                >
                  {t('admin.themeStudio.preview.sampleHeading')}
                </div>
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: `hsl(${activeTokens.accent} / 0.15)`,
                    color: `hsl(${activeTokens.accent})`,
                    border: `1px solid hsl(${activeTokens.accent} / 0.3)`,
                  }}
                >
                  {t('admin.themeStudio.preview.sampleBadge')}
                </span>
              </div>

              {/* Sample Card */}
              <div
                className="p-4 rounded-xl border shadow-sm space-y-2"
                style={{
                  backgroundColor: `hsl(${activeTokens.surface})`,
                  borderColor: `hsl(${activeTokens.border})`,
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-lg flex items-center justify-center text-white"
                    style={{ backgroundColor: `hsl(${activeTokens.primary})` }}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </div>
                  <h4 className="text-xs font-bold">{t('admin.themeStudio.preview.sampleCardTitle')}</h4>
                </div>
                <p
                  className="text-[11px] leading-relaxed"
                  style={{ color: `hsl(${activeTokens.textMuted})` }}
                >
                  {t('admin.themeStudio.preview.sampleCardDesc')}
                </p>
              </div>

              {/* Sample Input */}
              <div>
                <input
                  type="text"
                  readOnly
                  value=""
                  placeholder={t('admin.themeStudio.preview.sampleInputPlaceholder')}
                  className="w-full px-3 py-2 text-xs rounded-xl border shadow-inner transition-colors"
                  style={{
                    backgroundColor: `hsl(${activeTokens.surface})`,
                    borderColor: `hsl(${activeTokens.border})`,
                    color: `hsl(${activeTokens.textMain})`,
                  }}
                />
              </div>

              {/* Sample Buttons */}
              <div className="flex items-center gap-2.5 pt-1">
                <button
                  type="button"
                  className="flex-1 py-2 px-3 text-xs font-semibold rounded-xl text-white shadow-sm transition-all"
                  style={{
                    backgroundColor: `hsl(${activeTokens.primary})`,
                  }}
                >
                  {t('admin.themeStudio.preview.sampleButton')}
                </button>
                <button
                  type="button"
                  className="flex-1 py-2 px-3 text-xs font-semibold rounded-xl border transition-all"
                  style={{
                    borderColor: `hsl(${activeTokens.primary})`,
                    color: `hsl(${activeTokens.primary})`,
                    backgroundColor: `hsl(${activeTokens.primary} / 0.08)`,
                  }}
                >
                  {t('admin.themeStudio.preview.sampleSecondaryButton')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Theme Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl p-6 shadow-2xl space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-surface-border">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-bold text-sm text-foreground">
                  {t('admin.themeStudio.createModal.title')}
                </h3>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-muted-fg hover:text-foreground p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateTheme} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  {t('admin.themeStudio.createModal.nameLabel')}
                </label>
                <input
                  type="text"
                  required
                  value={newThemeName}
                  onChange={(e) => setNewThemeName(e.target.value)}
                  placeholder={t('admin.themeStudio.createModal.namePlaceholder')}
                  className="w-full px-3.5 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  {t('admin.themeStudio.createModal.descLabel')}
                </label>
                <textarea
                  rows={3}
                  value={newThemeDesc}
                  onChange={(e) => setNewThemeDesc(e.target.value)}
                  placeholder={t('admin.themeStudio.createModal.descPlaceholder')}
                  className="w-full px-3.5 py-2 text-xs bg-surface-elevated border border-surface-border rounded-xl text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-surface-border">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-muted-fg hover:text-foreground"
                >
                  {t('admin.themeStudio.actions.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={creating || !newThemeName.trim()}
                  className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary-hover rounded-xl shadow-glow-primary transition-all disabled:opacity-50"
                >
                  {creating ? t('common.loading') : t('admin.themeStudio.createModal.createButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export const ThemeStudioPage = ThemeStudioView;
